if (!customElements.get('b2b-quick-order')) {
  customElements.define(
    'b2b-quick-order',
    class B2BQuickOrder extends HTMLElement {
      constructor() {
        super();
        this.rows = Array.from(this.querySelectorAll('[data-quick-order-row]'));
        this.tableBody = this.querySelector('[data-quick-order-body]');
        this.searchInput = this.querySelector('[data-quick-order-search]');
        this.clearSearchButton = this.querySelector('[data-clear-search]');
        this.filterButtons = Array.from(this.querySelectorAll('[data-vehicle-filter]'));
        this.sortSelect = this.querySelector('[data-quick-order-sort]');
        this.inStockCheckbox = this.querySelector('[data-in-stock-only]');
        this.resultCount = this.querySelector('[data-result-count]');
        this.emptyState = this.querySelector('[data-empty-state]');
        this.selectedSummary = this.querySelector('[data-selection-summary]');
        this.submitButton = this.querySelector('[data-add-selected]');
        this.clearButton = this.querySelector('[data-clear-quantities]');
        this.message = this.querySelector('[data-quick-order-message]');
        this.messageText = this.querySelector('[data-quick-order-message-text]');
        this.messageSequence = 0;
        this.activeVehicleTag = '';
        this.minicart = document.querySelector('mini-cart');
        this.isSubmitting = false;
        this.submissionToken = 0;
        this.cartRefreshQueue = Promise.resolve();
        this.handlePageShow = this.handlePageShow.bind(this);
      }

      connectedCallback() {
        this.searchInput?.addEventListener('input', () => this.applyFilters());
        this.clearSearchButton?.addEventListener('click', () => {
          this.searchInput.value = '';
          this.searchInput.focus();
          this.applyFilters();
        });

        this.filterButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.activeVehicleTag = button.dataset.vehicleFilter || '';
            this.filterButtons.forEach((filterButton) => {
              const isActive = filterButton === button;
              filterButton.classList.toggle('is-active', isActive);
              filterButton.setAttribute('aria-pressed', isActive.toString());
            });
            this.applyFilters();
          });
        });

        this.sortSelect?.addEventListener('change', () => this.sortRows());
        this.inStockCheckbox?.addEventListener('change', () => this.applyFilters());
        this.addEventListener('click', (event) => this.handleQuantityButton(event));
        this.addEventListener('input', (event) => {
          if (event.target.matches('[data-quick-order-quantity]')) this.updateSelectionSummary();
        });
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-quick-order-quantity]')) this.normalizeQuantity(event.target);
        });
        this.clearButton?.addEventListener('click', () => this.clearQuantities());
        this.submitButton?.addEventListener('click', () => this.addSelectedItems());
        window.addEventListener('pageshow', this.handlePageShow);

        this.applyFilters();
        this.resetSubmissionState(false);
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.handlePageShow);
      }

      handlePageShow() {
        this.resetSubmissionState(true);
      }

      resetSubmissionState(clearMessage = true) {
        this.submissionToken += 1;
        this.isSubmitting = false;
        this.cartRefreshQueue = Promise.resolve();
        this.minicart = document.querySelector('mini-cart');
        this.setLoading(false);
        if (clearMessage) this.setMessage('');
        this.updateSelectionSummary();
      }

      normalize(value) {
        return (value || '').toString().trim().toLowerCase();
      }

      applyFilters() {
        const query = this.normalize(this.searchInput?.value);
        const tag = this.normalize(this.activeVehicleTag);
        const inStockOnly = Boolean(this.inStockCheckbox?.checked);
        let visibleCount = 0;

        this.rows.forEach((row) => {
          const searchMatch = !query || this.normalize(row.dataset.search).includes(query);
          const tags = this.normalize(row.dataset.tags)
            .split('|')
            .map((item) => item.trim())
            .filter(Boolean);
          const vehicleMatch = !tag || tags.includes(tag);
          const stockMatch = !inStockOnly || row.dataset.available === 'true';
          const visible = searchMatch && vehicleMatch && stockMatch;

          row.hidden = !visible;
          if (visible) visibleCount += 1;
        });

        if (this.resultCount) {
          const singular = this.dataset.resultSingular || 'SKU';
          const plural = this.dataset.resultPlural || 'SKUs';
          this.resultCount.textContent = `${visibleCount} ${visibleCount === 1 ? singular : plural}`;
        }

        this.emptyState?.toggleAttribute('hidden', visibleCount !== 0);
        this.clearSearchButton?.classList.toggle('is-visible', Boolean(query));
        this.sortRows();
      }

      sortRows() {
        if (!this.tableBody || !this.sortSelect) return;

        const [field, direction] = this.sortSelect.value.split(':');
        const multiplier = direction === 'desc' ? -1 : 1;
        const sortedRows = [...this.rows].sort((a, b) => {
          if (field === 'price') {
            return (Number(a.dataset.price) - Number(b.dataset.price)) * multiplier;
          }

          const aValue = this.normalize(a.dataset[field]);
          const bValue = this.normalize(b.dataset[field]);
          return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
        });

        sortedRows.forEach((row) => this.tableBody.appendChild(row));
      }

      handleQuantityButton(event) {
        const button = event.target.closest('[data-quantity-action]');
        if (!button) return;

        const row = button.closest('[data-quick-order-row]');
        const input = row?.querySelector('[data-quick-order-quantity]');
        if (!input || input.disabled) return;

        event.preventDefault();
        const action = button.dataset.quantityAction;
        const currentValue = Number(input.value) || 0;
        const minimum = Number(input.dataset.min) || 1;
        const increment = Number(input.dataset.increment) || 1;
        const maximum = input.dataset.max ? Number(input.dataset.max) : null;
        let nextValue;

        if (action === 'increase') {
          nextValue = currentValue === 0 ? minimum : currentValue + increment;
          if (maximum !== null) nextValue = Math.min(nextValue, maximum);
        } else {
          nextValue = currentValue <= minimum ? 0 : currentValue - increment;
        }

        input.value = Math.max(0, nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      normalizeQuantity(input) {
        const value = Math.max(0, Number.parseInt(input.value, 10) || 0);
        const minimum = Number(input.dataset.min) || 1;
        const increment = Number(input.dataset.increment) || 1;
        const maximum = input.dataset.max ? Number(input.dataset.max) : null;

        if (value === 0) {
          input.value = 0;
        } else {
          let normalizedValue = value < minimum ? minimum : value;
          const remainder = (normalizedValue - minimum) % increment;
          if (remainder !== 0) normalizedValue += increment - remainder;
          if (maximum !== null) normalizedValue = Math.min(normalizedValue, maximum);
          input.value = normalizedValue;
        }

        this.updateSelectionSummary();
      }

      selectedItems() {
        return this.rows
          .map((row) => {
            const input = row.querySelector('[data-quick-order-quantity]');
            return {
              id: Number(row.dataset.variantId),
              quantity: Number.parseInt(input?.value, 10) || 0,
              input,
            };
          })
          .filter((item) => item.quantity > 0 && item.input && !item.input.disabled);
      }

      updateSelectionSummary() {
        const selected = this.selectedItems();
        const units = selected.reduce((total, item) => total + item.quantity, 0);
        const skuLabel = selected.length === 1 ? this.dataset.selectedSkuSingular : this.dataset.selectedSkuPlural;
        const unitLabel = units === 1 ? this.dataset.selectedUnitSingular : this.dataset.selectedUnitPlural;

        if (this.selectedSummary) {
          this.selectedSummary.textContent = `${selected.length} ${skuLabel} / ${units} ${unitLabel}`;
        }
        if (this.submitButton) this.submitButton.disabled = this.isSubmitting || selected.length === 0;
        if (this.clearButton) this.clearButton.disabled = this.isSubmitting || selected.length === 0;
      }

      clearQuantities() {
        this.querySelectorAll('[data-quick-order-quantity]').forEach((input) => {
          input.value = 0;
        });
        this.setMessage('');
        this.updateSelectionSummary();
      }

      setMessage(message, isError = false) {
        if (!this.message) return;
        const messageTarget = this.messageText || this.message;
        const sequence = ++this.messageSequence;
        messageTarget.textContent = '';
        this.message.hidden = !message;
        this.message.classList.toggle('b2b-quick-order__message--success', Boolean(message) && !isError);
        this.message.classList.toggle('b2b-quick-order__message--error', isError);
        this.message.setAttribute('role', isError ? 'alert' : 'status');
        if (message) {
          window.requestAnimationFrame(() => {
            if (sequence === this.messageSequence && !this.message.hidden) messageTarget.textContent = message;
          });
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        if (isLoading) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-busy', 'true');
        } else {
          this.submitButton.removeAttribute('aria-busy');
        }
        this.submitButton.querySelector('.button-overlay-spinner')?.classList.toggle('hidden', !isLoading);
      }

      async refreshMiniCart(opener) {
        this.minicart = document.querySelector('mini-cart');
        if (!this.minicart?.updateSectionContents) return;

        try {
          await this.minicart.updateSectionContents();
          this.minicart.open?.(opener);
        } catch (error) {
          console.warn('The order was added, but the cart preview could not be refreshed.', error);
        }
      }

      queueMiniCartRefresh(opener) {
        this.cartRefreshQueue = this.cartRefreshQueue.then(
          () => this.refreshMiniCart(opener),
          () => this.refreshMiniCart(opener),
        );
      }

      async addSelectedItems() {
        if (this.isSubmitting) return;

        const selected = this.selectedItems();
        if (selected.length === 0) return;

        this.isSubmitting = true;
        const token = ++this.submissionToken;
        const submitted = selected.map(({ id, quantity, input }) => ({ id, quantity, input }));
        const skuCount = submitted.length;
        const unitCount = submitted.reduce((total, item) => total + item.quantity, 0);
        this.setLoading(true);
        this.setMessage('');
        this.updateSelectionSummary();

        const body = {
          items: submitted.map(({ id, quantity }) => ({ id, quantity })),
        };

        try {
          const response = await fetch(`${window.routes.cart_add_url}`, {
            ...fetchConfig(),
            headers: {
              ...fetchConfig().headers,
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          let state = {};
          try {
            state = await response.json();
          } catch (error) {
            throw new Error(this.dataset.genericError);
          }

          if (!response.ok || state.status) {
            throw new Error(state.description || state.message || this.dataset.genericError);
          }

          if (token !== this.submissionToken) return;

          submitted.forEach(({ input, quantity }) => {
            if ((Number.parseInt(input.value, 10) || 0) === quantity) input.value = 0;
          });
          const unitLabel = unitCount === 1 ? 'unit' : 'units';
          const skuLabel = skuCount === 1 ? 'SKU' : 'SKUs';
          this.setMessage(`${unitCount} ${unitLabel} across ${skuCount} ${skuLabel} added to cart.`);
          this.isSubmitting = false;
          this.setLoading(false);
          this.updateSelectionSummary();

          this.queueMiniCartRefresh(this.submitButton);
        } catch (error) {
          if (token !== this.submissionToken) return;

          const message = error.message && error.message !== this.dataset.genericError
            ? `${error.message} ${this.dataset.retryError}`
            : this.dataset.genericError;
          this.setMessage(message, true);
        } finally {
          if (token === this.submissionToken && this.isSubmitting) {
            this.isSubmitting = false;
            this.setLoading(false);
            this.updateSelectionSummary();
          }
        }
      }
    }
  );
}
